/**
 * /recurring-journals — Scheduled journal templates.
 *
 * Prepaid amortization, rent, subscription costs — any journal that
 * recurs on a fixed schedule. Shows upcoming run dates and lets an
 * accountant run a specific template or trigger all due templates.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Tooltip,
} from "@mui/material";
import RepeatIcon from "@mui/icons-material/Repeat";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";

export const Route = createFileRoute("/_auth/recurring-journals")({
  component: RecurringJournalsPage,
});

interface RecurringJournalLine {
  id: string;
  line_no: number;
  account_code: string;
  side: string;
  amount: string;
  description: string | null;
}

interface RecurringJournalTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  frequency: string;
  start_date: string;
  end_date: string | null;
  next_run_date: string;
  is_active: boolean;
  lines: RecurringJournalLine[];
}

interface RunResult {
  id: string;
  template_id: string;
  run_date: string;
  journal_id: string;
}

const recurringQueryOptions = () =>
  queryOptions({
    queryKey: ["recurring-journals"],
    queryFn: () => api<RecurringJournalTemplate[]>("/api/saas/recurring-journals"),
  });

const FREQ_COLOR: Record<string, "default" | "primary" | "secondary" | "info"> = {
  monthly: "primary",
  quarterly: "secondary",
  annual: "info",
  weekly: "default",
};

function RecurringJournalsPage() {
  const qc = useQueryClient();
  const q = useQuery(recurringQueryOptions());
  const [runResult, setRunResult] = useState<string | null>(null);

  const runOneMut = useMutation({
    mutationFn: (templateId: string) =>
      api<RunResult>(`/api/saas/recurring-journals/${templateId}/run`, {
        method: "POST",
        body: JSON.stringify({ run_date: new Date().toISOString().slice(0, 10) }),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["recurring-journals"] });
      setRunResult(`Journal #${r.journal_id.slice(0, 8)}… posted for run ${r.run_date}.`);
    },
  });

  const runAllMut = useMutation({
    mutationFn: () =>
      api<{ count: number }>("/api/saas/recurring-journals/run-all-due", { method: "POST" }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["recurring-journals"] });
      setRunResult(`${r.count} recurring journal(s) posted.`);
    },
  });

  const templates = q.data ?? [];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RepeatIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Recurring Journals</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            Scheduled journal templates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={runAllMut.isPending}
          onClick={() => runAllMut.mutate()}
        >
          {runAllMut.isPending ? "Running…" : "Run All Due"}
        </Button>
      </Box>

      {runResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRunResult(null)}>
          {runResult}
        </Alert>
      )}
      {(runOneMut.error || runAllMut.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(runOneMut.error ?? runAllMut.error)}
        </Alert>
      )}

      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No recurring journal templates. Create them via the AI chat or API.
                  </TableCell>
                </TableRow>
              )}
              {templates.map((t) => {
                const isDue = t.next_run_date <= today;
                return (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{t.code}</TableCell>
                    <TableCell fontWeight={500}>{t.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={t.frequency}
                        size="small"
                        color={FREQ_COLOR[t.frequency] ?? "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isDue ? "Due — ready to post" : ""}>
                        <Typography
                          variant="body2"
                          sx={{ color: isDue ? "warning.main" : "text.secondary", fontWeight: isDue ? 600 : 400 }}
                        >
                          {t.next_run_date}
                          {isDue && " ⚠"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                      {t.start_date} → {t.end_date ?? "∞"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={t.is_active ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{t.lines.length}</TableCell>
                    <TableCell>
                      {t.is_active && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={runOneMut.isPending}
                          onClick={() => runOneMut.mutate(t.id)}
                        >
                          Run
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
